/**
 * /arena/find — Gym Finder (basic list view).
 * Map view will land in Session 2.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Search, Plus, Camera, X, List, Map as MapIcon } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { ArenaShield } from "../../components/arena/ArenaAtoms";
import ArenaMapView from "../../components/arena/ArenaMapView";
import { toast } from "sonner";

export default function ArenaFind() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [city, setCity] = useState(user?.city || "");
  const [q, setQ] = useState("");
  const [gyms, setGyms] = useState([]);
  const [showCode, setShowCode] = useState(false);
  const [showRefer, setShowRefer] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [view, setView] = useState("list"); // list | map

  const load = () => {
    apiClient.get("/arena/find", { params: { city: city || undefined, q: q || undefined } })
      .then((r) => setGyms(r.data?.gyms || []))
      .catch(() => setGyms([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-find">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto px-4 py-3">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
            <ArenaShield />
            <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800, flex: 1 }}>Find a gym</span>
            <button onClick={() => setShowScanner(true)} aria-label="Scan QR" data-testid="open-scanner"
              style={{ width: 36, height: 36, borderRadius: "50%", background: "#F5C518", color: "#0C0C0B", border: "none", display: "grid", placeItems: "center" }}>
              <Camera size={16} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} data-testid="find-city"
              style={{ flex: 1, height: 40, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14 }} />
            <button onClick={load} aria-label="Search" data-testid="find-search"
              style={{ width: 40, height: 40, borderRadius: "50%", background: "#0C0C0B", color: "white", border: "none", display: "grid", placeItems: "center" }}>
              <Search size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-4 pb-32">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button onClick={() => setShowCode(true)} data-testid="open-code-entry"
            style={{ flex: 1, padding: "10px 12px", background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 100, fontSize: 12, fontWeight: 700, fontFamily: "Barlow" }}>
            Enter gym code
          </button>
          <button onClick={() => setShowRefer(true)} data-testid="open-refer"
            style={{ flex: 1, padding: "10px 12px", background: "transparent", border: "1px dashed var(--border, rgba(12,12,11,0.20))", borderRadius: 100, fontSize: 12, fontWeight: 700, fontFamily: "Barlow", color: "var(--text-secondary, #4A4845)" }}>
            <Plus size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} /> My gym not listed
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, marginBottom: 10 }}>
          <button onClick={() => setView("list")} data-testid="view-list"
            style={{ padding: "5px 10px", background: view === "list" ? "#0C0C0B" : "transparent", color: view === "list" ? "white" : "var(--text-secondary, #4A4845)", border: "1px solid", borderColor: view === "list" ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <List size={11} /> List
          </button>
          <button onClick={() => setView("map")} data-testid="view-map"
            style={{ padding: "5px 10px", background: view === "map" ? "#0C0C0B" : "transparent", color: view === "map" ? "white" : "var(--text-secondary, #4A4845)", border: "1px solid", borderColor: view === "map" ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <MapIcon size={11} /> Map
          </button>
        </div>

        {view === "map" ? (
          <ArenaMapView gyms={gyms} city={city} />
        ) : gyms.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>No gyms found here yet</div>
            <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>Refer your gym to earn 500 FitPoints when they activate.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {gyms.map((g) => (
              <button key={g.gym_id} onClick={() => navigate(`/arena/${g.gym_code}`)} data-testid={`gym-card-${g.gym_id}`}
                style={{ textAlign: "left", padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 14, display: "flex", gap: 12, alignItems: "stretch" }}>
                {g.photos?.[0] ? (
                  <img src={g.photos[0]} alt="" style={{ width: 84, height: 84, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 84, height: 84, borderRadius: 10, background: "var(--bg-inset, #D8D6D1)", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{g.gym_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 2, display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <MapPin size={10} /> {g.city}{g.address ? ` · ${g.address.slice(0, 40)}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                    <span style={{ padding: "2px 8px", background: "rgba(245,197,24,0.16)", color: "#A37B00", borderRadius: 100, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{g.gym_type}</span>
                    <span style={{ fontSize: 11, color: "var(--text-secondary, #4A4845)" }}>{g.rating > 0 ? `★ ${g.rating.toFixed(1)}` : "New"}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {showCode && <CodeEntryModal onClose={() => setShowCode(false)} />}
      {showRefer && <ReferModal onClose={() => setShowRefer(false)} />}
      {showScanner && <ScannerModal onClose={() => setShowScanner(false)} />}
    </div>
  );
}

function ScannerModal({ onClose }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    let stream = null;
    let raf = null;
    let cancelled = false;
    let detector = null;

    const start = async () => {
      // Check BarcodeDetector support
      if (!("BarcodeDetector" in window)) {
        setSupported(false);
        return;
      }
      try {
        // eslint-disable-next-line no-undef
        detector = new BarcodeDetector({ formats: ["qr_code"] });
      } catch {
        setSupported(false);
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        setError("Camera access denied. Use 'Enter gym code' instead.");
        return;
      }

      const tick = async () => {
        if (cancelled || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length > 0) {
            const url = codes[0].rawValue;
            // Match either /checkin/:gym_id/:token or /arena/:code
            const m1 = url.match(/\/checkin\/([^/]+)\/([^/?#]+)/);
            const m2 = url.match(/\/arena\/([A-Z0-9]{6})(?:[/?#]|$)/);
            if (m1) {
              cancelled = true;
              navigate(`/checkin/${m1[1]}/${m1[2]}`);
              return;
            }
            if (m2) {
              cancelled = true;
              navigate(`/arena/${m2[1]}`);
              return;
            }
          }
        } catch {
          // ignore frame errors
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    };
    start();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [navigate]);

  return (
    <div className="fixed inset-0 z-[90]" style={{ background: "#0C0C0B" }} data-testid="qr-scanner-modal">
      <button onClick={onClose} aria-label="Close" data-testid="scanner-close"
        style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.18)", color: "white", border: "none", display: "grid", placeItems: "center", zIndex: 2 }}>
        <X size={20} />
      </button>
      {!supported || error ? (
        <div style={{ height: "100vh", display: "grid", placeItems: "center", color: "white", padding: 24, textAlign: "center" }}>
          <div>
            <h2 style={{ fontFamily: "Barlow Condensed", fontSize: 24, fontWeight: 800 }}>{error || "QR scanning not supported on this browser"}</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", marginTop: 8 }}>
              Try Chrome on Android, Safari on iOS 17+, or use a 6-character code.
            </p>
          </div>
        </div>
      ) : (
        <>
          <video ref={videoRef} muted playsInline style={{ width: "100%", height: "100vh", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
            <div style={{ width: 240, height: 240, border: "3px solid #F5C518", borderRadius: 24, boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} />
          </div>
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 32, textAlign: "center", color: "white", fontSize: 14, fontWeight: 600 }}>
            Point at the gym's check-in QR
          </div>
        </>
      )}
    </div>
  );
}

function CodeEntryModal({ onClose }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const link = async (c) => {
    setBusy(true);
    try {
      await apiClient.post("/arena/link", { gym_code: c, method: "code" });
      navigate(`/arena/${c}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="code-entry-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Enter gym code</h3>
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>6-character code from your gym staff or printed QR</p>
        <input
          autoFocus value={code} onChange={(e) => {
            const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
            setCode(v);
            if (v.length === 6) link(v);
          }}
          maxLength={6} data-testid="code-input"
          style={{ width: "100%", height: 56, marginTop: 14, padding: "0 16px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900, letterSpacing: "0.2em", textAlign: "center", textTransform: "uppercase", outline: "none" }}
        />
        <button onClick={() => link(code)} disabled={busy || code.length < 6} data-testid="code-submit"
          style={{ width: "100%", height: 48, marginTop: 12, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700, opacity: code.length < 6 ? 0.5 : 1 }}>
          {busy ? "Linking…" : "Link"}
        </button>
      </div>
    </div>
  );
}

function ReferModal({ onClose }) {
  const [gymName, setGymName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!gymName) return toast.error("Gym name required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/arena/refer", { gym_name: gymName, gym_owner_phone: phone || null });
      const url = `${window.location.origin}/arena/join?ref=${data.referral_token}`;
      const text = `Hey! I use FitFrik for fitness tracking. They have a programme called Arena for gyms — get new members and retention analytics, free 14 days. ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      toast.success("Share link copied to WhatsApp");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create referral");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="refer-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Your gym is not on Arena yet</h3>
        <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>Invite them and earn <strong style={{ color: "#A37B00" }}>500 FitPoints</strong> when they activate.</p>
        <input placeholder="Gym name" value={gymName} onChange={(e) => setGymName(e.target.value)} data-testid="refer-gym-name"
          style={{ width: "100%", height: 44, marginTop: 12, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none" }} />
        <input placeholder="Owner WhatsApp (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
          style={{ width: "100%", height: 44, marginTop: 8, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none" }} />
        <button onClick={submit} disabled={busy} data-testid="refer-submit"
          style={{ width: "100%", height: 48, marginTop: 12, background: "#25D366", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 800 }}>
          Share with gym owner
        </button>
      </div>
    </div>
  );
}
